import { useState } from "react";
import { useTheme, mono, Card, Label, Btn } from "./appHelpers";

export default function DeadMassPage() {
  const C = useTheme();
  const [satMass, setSatMass] = useState(100);
  const [isp, setIsp] = useState(850);
  const alt = 450, yrs = 5;
  const G = 3.986e14, RE = 6371e3, g0 = 9.81;
  const rho = 4.71e-13 * Math.exp((500-alt)/48);
  const v = Math.sqrt(G/(RE+alt*1000));
  const area = satMass<=14?0.02:satMass<=50?0.06:satMass<=100?0.10:0.25;
  const Fd = 0.5*2.2*area*rho*v*v;
  const dv = Fd*3.156e7*yrs/satMass + 48.6*5;
  const ep_prop = satMass*(dv/(isp*g0))*1.020*0.90*0.92;
  const ion_prop = satMass*(dv/14715)*1.058;
  const ep_sys = ep_prop + 0.08*(satMass/100) + (isp>=300?0.5+0.001*(isp-300):0.15) + 0.4*Math.min(satMass/100,4) + 0.3 + 1.0*(satMass/100) + 0.4*(satMass/100) + 0.3*(satMass/100);
  const ion_solar = (150/10)*(satMass/100);
  const ion_bat = 150*35*60/3600/200*(satMass/100);
  const ion_sys = ion_prop + 2.5*(satMass/100) + 2.5*(satMass/100) + 1.2*(satMass/100) + ion_solar + ion_bat + 1.5*(satMass/100);
  const epWins = ep_sys < ion_sys;

  return (
    <div>
      <Card color="#ef4444">
        <Label>Configuration</Label>
        <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
          <div>
            <div style={{ ...mono, fontSize:9, color:C.muted, marginBottom:6 }}>SATELLITE MASS</div>
            <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
              {[8,14,25,50,100,200,500].map(m => <Btn key={m} active={satMass===m} color="#ef4444" onClick={() => setSatMass(m)}>{m}kg</Btn>)}
            </div>
          </div>
          <div>
            <div style={{ ...mono, fontSize:9, color:C.muted, marginBottom:6 }}>
              EP-ADCS ISP <span style={{ color:"#22c55e" }}>← baseline: 850s</span>
            </div>
            <div style={{ display:"flex", gap:4 }}>
              {[{v:70,l:"Cold 70s"},{v:150,l:"Warm 150s"},{v:300,l:"Mono 300s"},{v:850,l:"EP 850s ★"}].map(i => (
                <Btn key={i.v} active={isp===i.v} color="#ef4444" onClick={() => setIsp(i.v)}>{i.l}</Btn>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
        <Card color="#a855f7" style={{ margin:0 }}>
          <Label color="#a855f7">Ion Thruster System</Label>
          {[["Propellant (Isp=1500s)",ion_prop.toFixed(2)+"kg"],["Thruster + PPU",(2.5*satMass/100).toFixed(2)+"kg"],["Solar panels (150W)",ion_solar.toFixed(2)+"kg"],["Battery (eclipse)",ion_bat.toFixed(2)+"kg"],["Reaction wheels",(1.2*satMass/100).toFixed(2)+"kg"]].map(([l,val]) => (
            <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ ...mono, fontSize:10, color:C.text }}>{l}</span>
              <span style={{ ...mono, fontSize:10, color:"#a855f7", fontWeight:600 }}>{val}</span>
            </div>
          ))}
          <div style={{ ...mono, fontSize:18, color:"#ef4444", fontWeight:700, marginTop:10, textAlign:"right" }}>TOTAL: {ion_sys.toFixed(2)} kg</div>
        </Card>

        <Card color="#22c55e" style={{ margin:0 }}>
          <Label color="#22c55e">EP-ADCS System (Isp={isp}s)</Label>
          {[[`Propellant (Isp=${isp}s)`,ep_prop.toFixed(2)+"kg"],["EMMS hardware (4-axis)",(0.4*Math.min(satMass/100,4)).toFixed(2)+"kg"],["Thruster + tank",(isp>=300?0.5+0.001*(isp-300):0.15).toFixed(2)+"kg"],["Solar panels (0.5W)",(0.1*satMass/100).toFixed(2)+"kg"],["Reaction wheels","0.00 kg — eliminated"]].map(([l,val]) => (
            <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ ...mono, fontSize:10, color:C.text }}>{l}</span>
              <span style={{ ...mono, fontSize:10, color:"#22c55e", fontWeight:600 }}>{val}</span>
            </div>
          ))}
          <div style={{ ...mono, fontSize:18, color:epWins?"#22c55e":"#ef4444", fontWeight:700, marginTop:10, textAlign:"right" }}>TOTAL: {ep_sys.toFixed(2)} kg</div>
        </Card>
      </div>

      <div style={{ background:epWins?"#22c55e10":"#ef444410", border:`1px solid ${epWins?"#22c55e33":"#ef444433"}`, borderLeft:`3px solid ${epWins?"#22c55e":"#ef4444"}`, borderRadius:"0 6px 6px 0", padding:"12px 16px" }}>
        <div style={{ ...mono, fontSize:13, fontWeight:700, color:epWins?"#22c55e":"#ef4444" }}>
          {epWins ? `EP-ADCS wins by ${(ion_sys-ep_sys).toFixed(2)} kg — ${((ion_sys-ep_sys)/satMass*100).toFixed(1)}% of satellite mass freed` : `Ion wins by ${(ep_sys-ion_sys).toFixed(2)} kg — above crossover point (~350kg)`}
        </div>
        <div style={{ ...mono, fontSize:10, color:C.muted, marginTop:4 }}>
          {epWins ? "Ion's 150W solar panel requirement outweighs its propellant advantage at this scale." : "Above ~350kg, ion propellant saving outweighs the power hardware penalty."}
        </div>
      </div>
    </div>
  );
}
